import { Link, useLocation, useNavigate } from "react-router-dom";
import { Badge } from "./ui/badge";
import { Shield, Lock } from "lucide-react";
import circleNetworkLogo from "@/assets/circle-network-footer-logo.webp";
import appStoreComingSoon from "@/assets/app-store-coming-soon.png";

export const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    if (location.pathname === "/terms") {
      e.preventDefault();
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.pushState(null, "", `#${hash}`);
      }
    }
  };
  return <footer className="border-t bg-secondary/30">
      <div className="container mx-auto px-4 py-8 md:py-12">
        {/* Top Section - 2 Columns on Mobile, 5 Columns on Desktop */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 gap-x-3 md:gap-y-8 md:gap-x-6 mb-8 md:mb-12">
          {/* Logo & App Store Column */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center gap-3">
            <img 
              src={circleNetworkLogo} 
              alt="Circle Network" 
              className="w-20 h-20 object-contain"
            />
            <img 
              src={appStoreComingSoon} 
              alt="Coming Soon to App Store and Google Play" 
              className="w-40 object-contain"
            />
          </div>

          {/* Legal Column */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold mb-3 text-sm md:text-base">Legal</h4>
            <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
              <li><Link to="/terms#terms-of-service" className="hover:text-foreground transition-colors">Terms of Service</Link></li>
              <li><Link to="/terms#privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms#cookie-policy" className="hover:text-foreground transition-colors">Cookie Policy</Link></li>
              <li><Link to="/terms#cookie-preferences" className="hover:text-foreground transition-colors">Cookie Preferences</Link></li>
              <li><Link to="/terms#compliance" className="hover:text-foreground transition-colors">Compliance Info</Link></li>
            </ul>
          </div>

          {/* Marketplace Column */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold mb-3 text-sm md:text-base">Marketplace</h4>
            <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
              <li><Link to="/terms#seller-agreement" className="hover:text-foreground transition-colors">Seller Agreement</Link></li>
              <li><Link to="/terms#buyer-protection" className="hover:text-foreground transition-colors">Buyer Protection</Link></li>
              <li><Link to="/terms#prohibited-items" className="hover:text-foreground transition-colors">Prohibited Items</Link></li>
            </ul>
          </div>

          {/* Products Column */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold mb-3 text-sm md:text-base">Products</h4>
            <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
              <li><Link to="/browse" className="hover:text-foreground transition-colors">Browse all deals</Link></li>
              <li><Link to="/browse" className="hover:text-foreground transition-colors">Agent Tools</Link></li>
              <li><Link to="/browse" className="hover:text-foreground transition-colors">New arrivals</Link></li>
              <li><Link to="/browse" className="hover:text-foreground transition-colors">Ending soon</Link></li>
            </ul>
          </div>

          {/* Support Column */}
          <div className="text-center md:text-left">
            <h4 className="font-semibold mb-3 text-sm md:text-base">Support</h4>
            <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground transition-colors">About Us</Link></li>
              <li><Link to="/terms#dispute-resolution" onClick={(e) => handleAnchorClick(e, "dispute-resolution")} className="hover:text-foreground transition-colors">Dispute Resolution</Link></li>
              <li><Link to="/terms#refund-policy" onClick={(e) => handleAnchorClick(e, "refund-policy")} className="hover:text-foreground transition-colors">Refund Policy</Link></li>
              <li><Link to="/terms#contact-support" onClick={(e) => handleAnchorClick(e, "contact-support")} className="hover:text-foreground transition-colors">Contact Support</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Section - Enhanced Footer */}
        <div className="border-t pt-6 md:pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground">
            <p className="text-center md:text-left">© {new Date().getFullYear()} Circlenetwork.io. All rights reserved.</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="flex items-center gap-1 text-xs">
                <Shield className="w-3 h-3" />
                SSL Secured
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1 text-xs">
                <Lock className="w-3 h-3" />
                PCI Compliant
              </Badge>
              <span className="text-xs text-muted-foreground">256-bit encryption</span>
            </div>
          </div>
        </div>
      </div>
    </footer>;
};